/**
 * Standardized Supabase error handling utilities
 * Provides consistent error handling and response formatting across the application
 */

/**
 * Standard response format for all Supabase operations
 * @typedef {Object} StandardResponse
 * @property {boolean} success - Whether the operation succeeded
 * @property {any} data - The returned data (null if error)
 * @property {string} error - User-friendly error message (null if success)
 * @property {string} code - Error code for programmatic handling (null if success)
 */

/**
 * PostgREST error codes and their user-friendly messages
 */
const ERROR_MESSAGES = {
  'PGRST116': 'No records found',
  'PGRST204': 'No content returned',
  '23505': 'This record already exists',
  '23503': 'Related record not found',
  '42501': 'Permission denied',
  'PGRST301': 'Authentication required'
};

/**
 * Handle Supabase query errors with user-friendly messages
 * @param {Object} error - Supabase error object
 * @param {string} context - Context of the operation (e.g., "updating profile")
 * @param {Object} debugInfo - Additional debug information
 * @returns {StandardResponse}
 */
export const handleSupabaseError = (error, context = 'operation', debugInfo = {}) => {
  if (!error) {
    return {
      success: true,
      data: null,
      error: null,
      code: null
    };
  }

  const errorCode = error.code || 'UNKNOWN';
  const userMessage = ERROR_MESSAGES[errorCode] || error.message || 'An unexpected error occurred';
  
  // Log detailed error for debugging
  console.error(`❌ Supabase error during ${context}:`, {
    code: errorCode,
    message: error.message,
    details: error.details,
    hint: error.hint,
    ...debugInfo
  });

  return {
    success: false,
    data: null,
    error: `Error ${context}: ${userMessage}`,
    code: errorCode
  };
};

/**
 * Wrap Supabase query with standardized error handling
 * @param {Function} queryFn - Async function that performs the Supabase query
 * @param {string} context - Context description
 * @param {Object} debugInfo - Debug information
 * @returns {Promise<StandardResponse>}
 */
export const executeSupabaseQuery = async (queryFn, context, debugInfo = {}) => {
  try {
    const result = await queryFn();
    
    if (result.error) {
      return handleSupabaseError(result.error, context, debugInfo);
    }

    return {
      success: true,
      data: result.data,
      error: null,
      code: null
    };
  } catch (error) {
    console.error(`❌ Exception during ${context}:`, error);
    return {
      success: false,
      data: null,
      error: `Error ${context}: ${error.message}`,
      code: 'EXCEPTION'
    };
  }
};

/**
 * Validate required fields before executing query
 * @param {Object} fields - Object with field names and values
 * @param {string} context - Context description
 * @returns {StandardResponse|null} - Returns error response if validation fails, null if valid
 */
export const validateRequiredFields = (fields, context) => {
  for (const [fieldName, fieldValue] of Object.entries(fields)) {
    if (fieldValue === undefined || fieldValue === null || fieldValue === '') {
      console.error(`❌ Validation error in ${context}: Missing required field "${fieldName}"`);
      return {
        success: false,
        data: null,
        error: `${context}: Missing required field "${fieldName}"`,
        code: 'VALIDATION_ERROR'
      };
    }
  }
  return null;
};

/**
 * Check if user is authenticated
 * @param {Object} supabase - Supabase client instance
 * @returns {Promise<StandardResponse>} - Returns user data if authenticated, error otherwise
 */
export const checkAuthentication = async (supabase) => {
  try {
    const { data: { user }, error } = await supabase.auth.getUser();
    
    if (error || !user) {
      console.error('❌ Authentication check failed:', error);
      return {
        success: false,
        data: null,
        error: 'You must be logged in to perform this action',
        code: 'NOT_AUTHENTICATED'
      };
    }

    return {
      success: true,
      data: user,
      error: null,
      code: null
    };
  } catch (error) {
    console.error('❌ Authentication check exception:', error);
    return {
      success: false,
      data: null,
      error: 'Authentication verification failed',
      code: 'AUTH_ERROR'
    };
  }
};

/**
 * Safe single row fetch - uses maybeSingle() and handles not found gracefully
 * @param {Object} query - Supabase query builder
 * @param {string} resourceName - Name of the resource being fetched
 * @param {Object} filters - Filters used in the query (for debugging)
 * @returns {Promise<StandardResponse>}
 */
export const safeFetchSingle = async (query, resourceName, filters = {}) => {
  try {
    const { data, error } = await query.maybeSingle();
    
    if (error) {
      return handleSupabaseError(error, `fetching ${resourceName}`, { filters });
    }

    if (!data) {
      console.log(`ℹ️ ${resourceName} not found with filters:`, filters);
      return {
        success: false,
        data: null,
        error: `${resourceName} not found`,
        code: 'NOT_FOUND'
      };
    }

    return {
      success: true,
      data,
      error: null,
      code: null
    };
  } catch (error) {
    console.error(`❌ Exception fetching ${resourceName}:`, error, { filters });
    return {
      success: false,
      data: null,
      error: `Failed to fetch ${resourceName}`,
      code: 'EXCEPTION'
    };
  }
};

/**
 * Safe update operation - checks existence before updating
 * @param {Function} updateFn - Function that performs the update
 * @param {string} resourceName - Name of the resource being updated
 * @param {Object} debugInfo - Debug information
 * @returns {Promise<StandardResponse>}
 */
export const safeUpdate = async (updateFn, resourceName, debugInfo = {}) => {
  try {
    const { data, error } = await updateFn();
    
    if (error) {
      console.error(`❌ Update error for ${resourceName}:`, {
        error,
        debugInfo,
        errorCode: error.code,
        errorMessage: error.message
      });
      return handleSupabaseError(error, `updating ${resourceName}`, debugInfo);
    }

    if (!data || (Array.isArray(data) && data.length === 0)) {
      console.error(`❌ ${resourceName} not found for update:`, {
        debugInfo,
        dataReturned: data,
        message: 'Update query returned no rows - this might be an RLS issue or the record does not exist'
      });
      return {
        success: false,
        data: null,
        error: `${resourceName} not found or permission denied. Please ensure you're logged in and trying to update your own profile.`,
        code: 'UPDATE_FAILED'
      };
    }

    console.log(`✅ ${resourceName} updated successfully`);
    return {
      success: true,
      data: Array.isArray(data) ? data[0] : data,
      error: null,
      code: null
    };
  } catch (error) {
    console.error(`❌ Exception updating ${resourceName}:`, error, debugInfo);
    return {
      success: false,
      data: null,
      error: `Failed to update ${resourceName}: ${error.message}`,
      code: 'EXCEPTION'
    };
  }
};

/**
 * Safe insert operation
 * @param {Function} insertFn - Function that performs the insert
 * @param {string} resourceName - Name of the resource being inserted
 * @param {Object} debugInfo - Debug information
 * @returns {Promise<StandardResponse>}
 */
export const safeInsert = async (insertFn, resourceName, debugInfo = {}) => {
  try {
    const { data, error } = await insertFn();
    
    if (error) {
      return handleSupabaseError(error, `creating ${resourceName}`, debugInfo);
    }

    return {
      success: true,
      data: Array.isArray(data) ? data[0] : data,
      error: null,
      code: null
    };
  } catch (error) {
    console.error(`❌ Exception creating ${resourceName}:`, error, debugInfo);
    return {
      success: false,
      data: null,
      error: `Failed to create ${resourceName}`,
      code: 'EXCEPTION'
    };
  }
};

/**
 * Safe upsert operation
 * @param {Function} upsertFn - Function that performs the upsert
 * @param {string} resourceName - Name of the resource being upserted
 * @param {Object} debugInfo - Debug information
 * @returns {Promise<StandardResponse>}
 */
export const safeUpsert = async (upsertFn, resourceName, debugInfo = {}) => {
  try {
    const { data, error } = await upsertFn();
    
    if (error) {
      return handleSupabaseError(error, `upserting ${resourceName}`, debugInfo);
    }

    return {
      success: true,
      data: Array.isArray(data) ? data[0] : data,
      error: null,
      code: null
    };
  } catch (error) {
    console.error(`❌ Exception upserting ${resourceName}:`, error, debugInfo);
    return {
      success: false,
      data: null,
      error: `Failed to upsert ${resourceName}`,
      code: 'EXCEPTION'
    };
  }
};

